<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  AGENDA_POPOVER_ACTIONS,
  AGENDA_SATELLITE_TEXT,
  buildAgendaPopoverActions,
} from '../utils/agendaSatellitePresenter';

const props = defineProps({
  visit: { type: Object, default: () => ({}) },
  showConfirm: { type: Boolean, default: false },
  canCancel: { type: Boolean, default: true },
  canReschedule: { type: Boolean, default: true },
  isPool: { type: Boolean, default: false },
  includeMoreActions: { type: Boolean, default: true },
  startOpen: { type: Boolean, default: false },
});

const emit = defineEmits([
  'confirm',
  'cancel',
  'cancelGroup',
  'reschedule',
  'moreActions',
]);

const root = ref(null);
const isOpen = ref(props.startOpen);

const actions = computed(() =>
  buildAgendaPopoverActions({
    visit: props.visit,
    showConfirm: props.showConfirm,
    canCancel: props.canCancel,
    canReschedule: props.canReschedule,
    isPool: props.isPool,
    includeMoreActions: props.includeMoreActions,
  })
);

const close = () => {
  isOpen.value = false;
};

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const trigger = action => {
  close();
  switch (action.event) {
    case AGENDA_POPOVER_ACTIONS.CONFIRM:
      emit('confirm');
      break;
    case AGENDA_POPOVER_ACTIONS.CANCEL:
      emit('cancel');
      break;
    case AGENDA_POPOVER_ACTIONS.CANCEL_GROUP:
      emit('cancelGroup');
      break;
    case AGENDA_POPOVER_ACTIONS.RESCHEDULE:
      emit('reschedule');
      break;
    case AGENDA_POPOVER_ACTIONS.MORE_ACTIONS:
      emit('moreActions');
      break;
    default:
      break;
  }
};

const onOutsidePointerDown = event => {
  if (!isOpen.value || root.value?.contains(event.target)) return;
  close();
};

const onKeydown = event => {
  if (event.key === 'Escape') close();
};

onMounted(() => {
  window.addEventListener('pointerdown', onOutsidePointerDown);
  window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', onOutsidePointerDown);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div ref="root" class="relative shrink-0">
    <button
      type="button"
      class="grid size-8 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-primary-50 hover:text-lais-primary"
      :aria-label="AGENDA_SATELLITE_TEXT.POPOVER_ARIA_LABEL"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      @click.stop="toggle"
    >
      <span class="i-lucide-more-horizontal size-4" aria-hidden="true" />
    </button>

    <div
      v-if="isOpen"
      class="absolute right-0 top-9 z-40 min-w-[212px] overflow-hidden rounded-lais border border-lais-border bg-white py-1 shadow-lais-pop"
      role="menu"
    >
      <button
        v-for="action in actions"
        :key="action.key"
        type="button"
        class="flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-semibold transition-colors hover:bg-lais-primary-50"
        :class="[
          action.tone === 'danger'
            ? 'text-rose-700 hover:bg-rose-50'
            : 'text-lais-text',
          action.separatorBefore ? 'border-t border-lais-border' : '',
        ]"
        role="menuitem"
        @click.stop="trigger(action)"
      >
        <span class="size-4 shrink-0" :class="action.icon" aria-hidden="true" />
        <span class="min-w-0">{{ action.label }}</span>
      </button>
    </div>
  </div>
</template>
